import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Mail, Search, Upload, UploadCloud } from 'lucide-react';
import { IMPORT_SOURCES, IMPORT_STATUSES } from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTablePagination } from '@/components/DataTablePagination';
import { StatusBadge } from '@/components/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { importsApi } from '@/lib/api/endpoints';
import {
  IMPORT_SOURCE_LABEL,
  IMPORT_STATUS_LABEL,
  IMPORT_STATUS_VARIANT,
  fmtDateTime,
  fmtDay,
  fmtInt,
  fmtMoney,
} from '@/lib/format';
import { useCan } from '@/features/auth/store';
import { ImportDialog } from './ImportDialog';

const ALL = '__all__';
const PAGE_SIZE = 25;

export function ImportsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canManage = useCan('imports.manage');
  const status = params.get('status') ?? '';
  const source = params.get('source') ?? '';
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);
  const set = (k: string, v: string | undefined) => {
    const next = new URLSearchParams(params);
    if (!v || v === ALL) next.delete(k);
    else next.set(k, v);
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const list = useQuery({
    queryKey: ['imports', { status, source, q, page }],
    queryFn: () =>
      importsApi.list({
        status: status || undefined,
        source: source || undefined,
        q: q || undefined,
        page,
        pageSize: PAGE_SIZE,
        sortBy: 'createdAt',
        sortDir: 'desc',
      }),
    placeholderData: (p) => p,
  });
  const conflicts = useQuery({
    queryKey: ['imports', 'conflicts'],
    queryFn: () => importsApi.list({ status: 'conflict', pageSize: 50 }),
  });
  const [open, setOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title="Importações"
        description="Cada relatório da BuyGoods importado, pelo painel ou pela caixa de e-mail. Só as importações ativas entram no faturamento."
        actions={
          canManage && (
            <Button onClick={() => setOpen(true)}>
              <Upload /> Importar relatório
            </Button>
          )
        }
      />

      {conflicts.data && conflicts.data.total > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="size-4" />
          <AlertTitle>{conflicts.data.total} importação(ões) em conflito</AlertTitle>
          <AlertDescription>
            Chegaram pela caixa de e-mail com um período que cruza uma importação ativa diferente.
            Abra cada uma e escolha "Ativar esta" (substitui as que cruzam) ou exclua.
            <ul className="mt-1 list-disc pl-4">
              {conflicts.data.items.slice(0, 5).map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    className="underline"
                    onClick={() => navigate(`/importacoes/${i.id}`)}
                  >
                    {i.fileName} · {fmtDay(i.periodStart)} – {fmtDay(i.periodEnd)}
                  </button>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={status || ALL} onValueChange={(v) => set('status', v)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {IMPORT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {IMPORT_STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Origem</Label>
          <Select value={source || ALL} onValueChange={(v) => set('source', v)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas</SelectItem>
              {IMPORT_SOURCES.map((s) => (
                <SelectItem key={s} value={s}>
                  {IMPORT_SOURCE_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-56 flex-1 space-y-1">
          <Label className="text-xs" htmlFor="imports-q">
            Buscar
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="imports-q"
              placeholder="Arquivo, título, usuário ou assunto do e-mail"
              className="pl-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && set('q', searchInput.trim())}
              onBlur={() => searchInput.trim() !== q && set('q', searchInput.trim())}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        {list.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : list.data?.items.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Arquivo</TableHead>
                  <TableHead className="text-right">Linhas</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Líquido</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Importado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.items.map((i) => (
                  <TableRow
                    key={i.id}
                    className={
                      i.status === 'superseded' ? 'cursor-pointer opacity-60' : 'cursor-pointer'
                    }
                    onClick={() => navigate(`/importacoes/${i.id}`)}
                  >
                    <TableCell className="whitespace-nowrap font-medium">
                      {fmtDay(i.periodStart)}
                      {i.periodStart !== i.periodEnd && ` – ${fmtDay(i.periodEnd)}`}
                    </TableCell>
                    <TableCell className="max-w-72">
                      <p className="truncate" title={i.fileName}>
                        {i.fileName}
                      </p>
                      {i.warnings.length > 0 && (
                        <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                          <AlertTriangle className="size-3" /> {i.warnings.length} aviso(s)
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(i.rowCount)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(i.totals.grossSales)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(i.totals.netSales)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={IMPORT_STATUS_LABEL[i.status]}
                        className={IMPORT_STATUS_VARIANT[i.status]}
                      />
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="flex items-center gap-1">
                        {i.source === 'email' ? (
                          <Mail className="size-3.5 text-muted-foreground" />
                        ) : (
                          <UploadCloud className="size-3.5 text-muted-foreground" />
                        )}
                        {IMPORT_SOURCE_LABEL[i.source]}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {i.createdByName ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {fmtDateTime(i.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              total={list.data.total}
              onPageChange={(p) => set('page', String(p))}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Upload}
              title="Nenhuma importação"
              description="Exporte o relatório de afiliados na BuyGoods (Reports → Affiliates) e envie aqui. Pode ser o mês inteiro, um período ou um dia."
              action={
                canManage && (
                  <Button onClick={() => setOpen(true)}>
                    <Upload /> Importar relatório
                  </Button>
                )
              }
            />
          </div>
        )}
      </div>

      <ImportDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
